<template>
  <BaseModal
    :is-visible="isVisible"
    :title="exam?.exam_name || 'Exam Details'"
    subtitle="View exam information and content"
    :icon="FileText"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <div v-if="exam" class="space-y-6">
      <!-- Exam Preview Section -->
      <div class="border-t pt-6">
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Exam Preview
        </label>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div class="bg-blue-50 dark:bg-blue-900/30 rounded-xl p-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300">
                Total Questions
              </span>
              <span class="text-base font-semibold text-blue-600 dark:text-blue-400">
                {{ exam.exam_content?.questions?.length || 0 }}
              </span>
            </div>
          </div>

          <div class="bg-green-50 dark:bg-green-900/30 rounded-xl p-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300">
                Passing Rate
              </span>
              <span class="text-base font-semibold text-green-600 dark:text-green-400">
                {{ exam.passing_rate }}%
              </span>
            </div>
          </div>

          <div class="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300"> Time Limit </span>
              <span class="text-base font-semibold text-purple-600 dark:text-purple-400">
                {{ exam.time_limit ? `${exam.time_limit} min` : 'No limit' }}
              </span>
            </div>
          </div>

          <div class="bg-orange-50 dark:bg-orange-900/30 rounded-xl p-3">
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300"> Status </span>
              <span :class="exam.exam_status === 1 ? 'badge-success' : 'badge-warning'">
                {{ exam.exam_status === 1 ? 'Active' : 'Draft' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Basic Information Section -->
      <div class="border-t pt-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <!-- Exam Name -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Exam Name
            </label>
            <div
              class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center"
            >
              {{ exam.exam_name }}
            </div>
          </div>
          <!-- Created Date -->
          <div>
            <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Created Date
            </label>
            <div
              class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center"
            >
              {{ formatDate(exam.created_at) }}
            </div>
          </div>
        </div>
        <!-- Module (full width) -->
        <div class="mt-4">
          <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Module
          </label>
          <div
            class="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white min-h-[2.75rem] flex items-center"
          >
            {{ getModuleName(exam.module_id) }}
          </div>
        </div>
      </div>

      <!-- Questions Section -->
      <div class="border-t pt-6">
        <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          Questions ({{ exam.exam_content?.questions?.length || 0 }})
        </label>

        <div v-if="!exam.exam_content?.questions?.length" class="text-center py-8">
          <HelpCircle class="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p class="text-gray-500 dark:text-gray-400">
            This exam doesn't contain any questions yet.
          </p>
        </div>

        <!-- Modern, clean question cards -->
        <div v-else class="flex flex-col gap-6 max-h-[700px] overflow-y-auto pr-2 pb-6">
          <div
            v-for="(question, index) in exam.exam_content.questions"
            :key="question.id"
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 flex gap-4 items-start"
          >
            <!-- Icon/number -->
            <div class="flex-shrink-0">
              <div class="bg-blue-50 dark:bg-blue-900/30 rounded-xl h-12 w-12 flex items-center justify-center">
                <span class="text-2xl font-bold text-blue-600 dark:text-blue-400">{{ index + 1 }}</span>
              </div>
            </div>
            <!-- Content -->
            <div class="flex-1">
              <div class="flex items-center gap-2 mb-2">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400 tracking-wide">
                  {{
                    question.type === 'multiple_choice'
                      ? 'Multiple Choice'
                      : question.type === 'true_false'
                      ? 'True or False'
                      : 'Essay'
                  }}
                </span>
              </div>
              <!-- Question text, not in input, styled cleanly -->
              <div class="mb-3 text-lg text-gray-800 dark:text-gray-200 font-medium leading-relaxed">
                {{ question.question }}
              </div>
              <!-- Multiple Choice Options -->
              <div v-if="question.type === 'multiple_choice' && question.options" class="mb-3">
                <ul class="space-y-2">
                  <li
                    v-for="(option, optIndex) in question.options"
                    :key="optIndex"
                    class="flex items-center gap-2"
                  >
                    <span
                      class="inline-flex items-center justify-center h-6 w-6 rounded-full border border-gray-300 dark:border-gray-600 text-xs font-semibold"
                      :class="{
                        'bg-blue-500 text-white border-blue-500': exam.answer_key?.[question.id] === option.id,
                        'bg-gray-100 dark:bg-gray-800': exam.answer_key?.[question.id] !== option.id
                      }"
                    >
                      {{ String.fromCharCode(65 + optIndex) }}
                    </span>
                    <span class="text-gray-900 dark:text-gray-200 text-sm">
                      {{ option.text }}
                    </span>
                  </li>
                </ul>
              </div>
              <!-- Correct Answer -->
              <div class="mt-4">
                <label class="block text-xs font-semibold text-green-600 dark:text-green-400 mb-1">
                  Correct Answer
                </label>
                <div
                  class="px-4 py-2 rounded-lg font-medium bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200"
                >
                  <template v-if="question.type === 'multiple_choice'">
                    {{ getMultipleChoiceAnswer(question) }}
                  </template>
                  <template v-else-if="question.type === 'true_false'">
                    {{ exam.answer_key?.[question.id] }}
                  </template>
                  <template v-else-if="question.type === 'essay'">
                    {{ exam.answer_key?.[question.id] || 'No sample answer provided' }}
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p class="text-gray-500 dark:text-gray-400">Loading exam details...</p>
    </div>

    <template #footer>
      <BaseButton text="Close" variant="secondary" @click="closeModal" />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { FileText, HelpCircle } from 'lucide-vue-next'
import type { Exam } from '@/services/examService'
import type { Module } from '@/services/modules'

interface Props {
  isVisible: boolean
  exam: Exam | null
  modules?: Module[]
}

interface Emits {
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Computed properties
const modulesList = computed(() => props.modules || [])

const currentCard = ref(0)

// Methods
const getModuleName = (moduleId: number) => {
  const module = modulesList.value.find(m => m.module_id === moduleId)
  return module ? module.name : 'Unknown Module'
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A'
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  } catch {
    return 'Invalid date'
  }
}

const getMultipleChoiceAnswer = (question: any) => {
  if (!props.exam?.answer_key || !question.options) return 'Not specified'

  const correctAnswerId = props.exam.answer_key[question.id]
  if (!correctAnswerId) return 'Not specified'

  const correctOption = question.options.find((opt: any) => opt.id === correctAnswerId)
  if (!correctOption) return 'Not specified'

  const optionIndex = question.options.findIndex((opt: any) => opt.id === correctAnswerId)
  const optionLetter = String.fromCharCode(65 + optionIndex)

  return `${optionLetter}. ${correctOption.text}`
}

const closeModal = () => {
  emit('close')
}

// Watchers
watch(() => props.isVisible, (val) => { if (val) currentCard.value = 0 }) // Reset on open

</script>

<style scoped>
.badge-success {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #dcfce7;
  color: #166534;
}

.dark .badge-success {
  background-color: rgba(34, 197, 94, 0.3);
  color: #86efac;
}

.badge-warning {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #fef3c7;
  color: #92400e;
}

.dark .badge-warning {
  background-color: rgba(245, 158, 11, 0.3);
  color: #fde047;
}

.question-card {
  background: var(--tw-bg-opacity) #fff;
  border-radius: 0.75rem;
  box-shadow: 2px 2px 8px 2px rgba(0,0,0,0.04);
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  transition: box-shadow 0.2s;
}

.dark .question-card {
  background: #1e293b;
  box-shadow: 2px 2px 8px 2px rgba(30,41,59,0.15);
}

.alt-question-card {
  background: #f9fafb;
  border-radius: 1rem;
  box-shadow: 0 2px 12px 0 rgba(30,41,59,0.07);
  padding: 1.25rem 1.5rem 1.25rem 1.5rem;
  border: 1px solid #e5e7eb;
  position: relative;
  transition: box-shadow 0.2s;
  min-width: 350px;
  max-width: 350px;
  flex: 0 0 350px;
  margin-bottom: 0;
  margin-right: 0;
}

.dark .alt-question-card {
  background: #1e293b;
  border: 1px solid #334155;
  box-shadow: 0 2px 12px 0 rgba(30,41,59,0.18);
}

.alt-card-bar {
  height: 2rem;
  border-radius: 0.75rem 0.75rem 0 0;
  background: linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%);
  padding: 0.25rem 1rem;
  margin: -1.25rem -1.5rem 1rem -1.5rem;
}

.dark .alt-card-bar {
  background: linear-gradient(90deg, #2563eb 0%, #60a5fa 100%);
}

.alt-card-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  color: #2563eb;
  font-weight: bold;
  border-radius: 9999px;
  width: 1.75rem;
  height: 1.75rem;
  font-size: 1rem;
  box-shadow: 0 1px 4px 0 rgba(59,130,246,0.10);
}

.dark .alt-card-num {
  background: #1e293b;
  color: #93c5fd;
}

.alt-answer-label {
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 0.5rem;
  padding: 0.25rem 0.75rem;
  font-weight: 500;
  font-size: 0.95rem;
  display: inline-block;
}

.dark .alt-answer-label {
  background: #0c4a6e;
  color: #bae6fd;
}

.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}
.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Webkit */
}

.modern-flashcard {
  background: #fff;
  border-radius: 1.25rem;
  box-shadow: 0 4px 32px 0 rgba(30,41,59,0.10);
  border: 1px solid #e5e7eb;
  min-width: 340px;
  max-width: 400px;
  width: 100%;
  margin: 0 auto;
  position: relative;
  transition: box-shadow 0.2s;
  min-height: 270px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dark .modern-flashcard {
  background: #1e293b;
  border: 1px solid #334155;
  box-shadow: 0 4px 32px 0 rgba(30,41,59,0.25);
}

.modern-card-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #3b82f6;
  color: #fff;
  font-weight: bold;
  border-radius: 9999px;
  width: 2rem;
  height: 2rem;
  font-size: 1rem;
  box-shadow: 0 1px 4px 0 rgba(59,130,246,0.10);
}

.dark .modern-card-num {
  background: #2563eb;
  color: #bae6fd;
}

.modern-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  background: #f3f4f6;
  color: #3b82f6;
  font-weight: 600;
  border: 1.5px solid #cbd5e1;
  font-size: 0.95rem;
  transition: background 0.2s, color 0.2s;
}

.modern-option-correct {
  background: #3b82f6;
  color: #fff;
  border-color: #3b82f6;
}

.dark .modern-option {
  background: #334155;
  color: #60a5fa;
  border-color: #475569;
}

.dark .modern-option-correct {
  background: #2563eb;
  color: #fff;
  border-color: #2563eb;
}

.modern-answer-label {
  background: #e0f2fe;
  color: #0369a1;
  border-radius: 0.5rem;
  padding: 0.25rem 0.75rem;
  font-weight: 500;
  font-size: 0.95rem;
  display: inline-block;
}

.dark .modern-answer-label {
  background: #0c4a6e;
  color: #bae6fd;
}

.modern-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #f3f4f6;
  border: none;
  border-radius: 9999px;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px 0 rgba(30,41,59,0.08);
  z-index: 2;
}

.modern-arrow.left { left: -1.5rem; }
.modern-arrow.right { right: -1.5rem; }

.modern-arrow:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dark .modern-arrow {
  background: #334155;
}

.carousel-viewport {
  width: 100%;
  max-width: 420px;
  overflow: hidden;
  position: relative;
  margin: 0 auto 2rem auto;
  height: 370px;
}
.carousel-track {
  display: flex;
  transition: transform 0.45s cubic-bezier(.4,1,.4,1);
  align-items: center;
  height: 100%;
}
.carousel-card {
  min-width: 320px;
  max-width: 320px;
  margin-right: 24px;
  border-radius: 1.25rem;
  box-shadow: 0 4px 32px 0 rgba(30,41,59,0.10);
  border: none;
  padding: 2rem 1.5rem 1.5rem 1.5rem;
  opacity: 0.5;
  /* Remove or override the scaling below */
  transform: scale(1);
  transition: box-shadow 0.3s, opacity 0.3s;
  color: #fff;
  position: relative;
  z-index: 1;
  cursor: pointer;
  user-select: none;
}
.carousel-card:last-child {
  margin-right: 0;
}
.carousel-card--active {
  box-shadow: 0 8px 32px 0 rgba(30,41,59,0.18);
  opacity: 1;
  /* Remove or override the scaling below */
  transform: scale(1);
  z-index: 2;
}
.carousel-card-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.25);
  color: #fff;
  font-weight: bold;
  border-radius: 9999px;
  width: 2rem;
  height: 2rem;
  font-size: 1rem;
  box-shadow: 0 1px 4px 0 rgba(59,130,246,0.10);
}
.carousel-option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 9999px;
  background: rgba(255,255,255,0.15);
  color: #fff;
  font-weight: 600;
  border: 1.5px solid rgba(255,255,255,0.25);
  font-size: 0.95rem;
  transition: background 0.2s, color 0.2s;
}
.carousel-option-correct {
  background: #22d3ee;
  color: #fff;
  border-color: #22d3ee;
}
.carousel-answer-label {
  background: rgba(255,255,255,0.18);
  color: #fff;
  border-radius: 0.5rem;
  padding: 0.25rem 0.75rem;
  font-weight: 500;
  font-size: 0.95rem;
  display: inline-block;
  box-shadow: 0 1px 4px 0 rgba(0,0,0,0.04);
}
.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: #fff;
  border: none;
  border-radius: 9999px;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 8px 0 rgba(30,41,59,0.08);
  z-index: 10;
  opacity: 0.85;
}
.carousel-arrow.left { left: -1.5rem; }
.carousel-arrow.right { right: -1.5rem; }
.carousel-arrow:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}
@media (max-width: 600px) {
  .carousel-viewport {
    max-width: 98vw;
    height: 340px;
  }
  .carousel-card, .carousel-card--active {
    min-width: 90vw;
    max-width: 90vw;
    padding: 1.2rem 0.7rem 1rem 0.7rem;
  }
  .carousel-arrow.left { left: -0.5rem; }
  .carousel-arrow.right { right: -0.5rem; }
}
</style>
